import React, { useCallback, useRef, useState } from 'react';
import isNaN from 'lodash/isNaN';

import { SelectMaterialsModalButtonRequiredProps } from './SelectMaterialsModalButton.types';
import { ErrorMessage, I18nText } from '../../../types';
import { FetchMaterialsSetByUserIdScopeType } from '../../../main/materialsSets/materialsSetsTypes';

import {
  SelectMaterialsContent,
  SelectMaterialsContentOnMaterialsSelect
} from '../../../main/materials/components/contents/SelectMaterialsContent';

import { SimpleModalButton } from '../../buttons/SimpleModalButton';

import { TEMP_SELECTED_MATERIAL_LAST_ID } from '../../../main/materialsSets/materialsSetsConstants';

const SELECT_MATERIALS_BUTTON = 'select-materials-button';

interface SelectMaterialsModalButtonBaseProps {
  value: string[];
  disabled?: boolean;
  submitErrorMessage?: ErrorMessage;
  resetOnClose?: boolean;
  materialsSetScope?: FetchMaterialsSetByUserIdScopeType;
}

interface SelectMaterialsModalButtonWithTextProps {
  buttonText: string;
  i18nText?: never;
}

interface SelectMaterialsModalButtonWithI18nTextProps {
  buttonText?: never;
  i18nText: I18nText;
}

interface SelectMaterialsModalButtonWithOnSubmitProps {
  onSubmit: (value: string[]) => Promise<unknown>;
  onChange?: never;
}

interface SelectMaterialsModalButtonWithOnChangeProps {
  onSubmit?: never;
  onChange: (value: string[]) => void;
}

type SelectMaterialsModalButtonProps = SelectMaterialsModalButtonBaseProps &
  (
    | SelectMaterialsModalButtonWithTextProps
    | SelectMaterialsModalButtonWithI18nTextProps
  ) &
  (
    | SelectMaterialsModalButtonWithOnChangeProps
    | SelectMaterialsModalButtonWithOnSubmitProps
  );

function SelectMaterialsModalButton({
  value,
  disabled,
  onChange,
  onSubmit,
  className,
  i18nTitle,
  icon,
  iconClassName,
  modalIcon,
  i18nSubmitText,
  tooltipPlacement,
  tooltipI18nText,
  i18nText,
  buttonText,
  resetOnClose,
  withoutInitialFocus,
  materialsSetScope
}: SelectMaterialsModalButtonProps & SelectMaterialsModalButtonRequiredProps) {
  const [selectedMaterialIdsBeforeOpen, setSelectedMaterialIdsBeforeOpen] =
    useState<string[]>(value);
  const [selectedMaterialIds, setSelectedMaterialIds] =
    useState<string[]>(value);
  const [imageLightboxOpen, setImageLightboxOpen] = useState<boolean>(false);
  const fetchedSelectedMaterialIds = useRef<boolean>(true);
  const loadingSelectedMaterialIds = useRef<boolean>(false);

  const handleMaterialsSelect =
    useCallback<SelectMaterialsContentOnMaterialsSelect>(
      (selectedMaterials) => {
        fetchedSelectedMaterialIds.current = selectedMaterials.every(
          (selectedMaterial) =>
            isNaN(+selectedMaterial.id) ||
            +selectedMaterial.id < TEMP_SELECTED_MATERIAL_LAST_ID
        );

        setSelectedMaterialIds(
          selectedMaterials.map(
            (selectedMaterial) => selectedMaterial.id as string
          )
        );
      },
      [setSelectedMaterialIds]
    );

  const handleOpen = useCallback<() => void>(() => {
    fetchedSelectedMaterialIds.current = true;
    loadingSelectedMaterialIds.current = false;
    setSelectedMaterialIdsBeforeOpen(selectedMaterialIds);
  }, [selectedMaterialIds, setSelectedMaterialIdsBeforeOpen]);

  const handleCancel = useCallback<() => void>(
    () => setSelectedMaterialIds(selectedMaterialIdsBeforeOpen),
    [selectedMaterialIdsBeforeOpen, setSelectedMaterialIds]
  );

  const handleAfterSubmit = useCallback<() => void>(() => {
    if (resetOnClose) {
      setSelectedMaterialIdsBeforeOpen([]);
    } else {
      setSelectedMaterialIdsBeforeOpen(selectedMaterialIds);
    }
  }, [resetOnClose, selectedMaterialIds]);

  const handleSubmit = useCallback<() => Promise<void>>(async () => {
    if (onChange) {
      onChange(selectedMaterialIds);
      handleAfterSubmit();
    } else {
      return onSubmit?.(selectedMaterialIds).then(() => handleAfterSubmit());
    }
  }, [onChange, selectedMaterialIds, handleAfterSubmit, onSubmit]);

  const checkAndSubmit = useCallback<() => Promise<void>>(async () => {
    while (!fetchedSelectedMaterialIds.current) {
      loadingSelectedMaterialIds.current = true;
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    loadingSelectedMaterialIds.current = false;

    return handleSubmit();
  }, [fetchedSelectedMaterialIds, handleSubmit]);

  const togglePreventModalClose = useCallback<() => void>(
    () => setImageLightboxOpen((prevState) => !prevState),
    [setImageLightboxOpen]
  );

  return (
    <SimpleModalButton
      dataGa={SELECT_MATERIALS_BUTTON}
      className={className}
      childrenClassName="flex-1 flex overflow-hidden"
      i18nTitle={i18nTitle}
      icon={icon}
      iconClassName={iconClassName}
      modalIcon={modalIcon}
      modalSize="full"
      {...(i18nText ? { i18nText } : { buttonText })}
      i18nSubmitText={i18nSubmitText}
      disabled={disabled}
      isLoading={disabled || loadingSelectedMaterialIds.current}
      tooltipPlacement={tooltipPlacement}
      tooltipI18nText={tooltipI18nText}
      preventModalClose={imageLightboxOpen}
      onOpen={handleOpen}
      onCancel={handleCancel}
      onSubmit={checkAndSubmit}
      withoutInitialFocus={withoutInitialFocus}
    >
      <SelectMaterialsContent
        initialLimit={48}
        onMaterialsSelect={handleMaterialsSelect}
        togglePreventModalClose={togglePreventModalClose}
        materialsSetScope={materialsSetScope}
      />
    </SimpleModalButton>
  );
}

export default SelectMaterialsModalButton;
